import {
  allowedDomains,
  iframePermissions,
  LasClientMessage,
  listenForExitMessages,
} from '.';
import {
  closeIframe,
  getGwClientIframe,
  openUrlInIframe,
} from '../../utils/iframe';
import { ColorScheme, toCssColorScheme } from '../constants';
import createBrowserHistoryHelper from './browserHistory';
import { hideLoader } from './loader';

export const launchInIframe = async (
  url: string,
  {
    isNestedFlow = false,
    signal,
    colorScheme,
  }: {
    isNestedFlow?: boolean;
    signal?: AbortSignal;
    colorScheme?: ColorScheme;
  } = {},
) => {
  const resetHistory = createBrowserHistoryHelper();

  // open las_client
  const iframeEl = getGwClientIframe({
    permissions: iframePermissions,
    colorScheme: toCssColorScheme(colorScheme),
  });
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const stopExternalFlows = listenForExternalFlows(iframeEl, colorScheme);
  // setup for listeners to resolve on postmessage from las_client
  const exitPromise = listenForExitMessages(iframeEl, signal);
  // an already-aborted or raced signal can reject this before we await it
  // below; attach a handler now so the rejection never floats unhandled. The
  // real handling still happens at the `await exitPromise` try/finally.
  exitPromise.catch(() => {});
  await openUrlInIframe(iframeEl, url, {
    skipActiveElement: isNestedFlow,
  });

  // close loader
  hideLoader();

  // disable scroll, save previous value to restore correctly at any nesting depth
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  let clientMessage: MessageEvent<LasClientMessage>;
  try {
    // wait for listeners to get some message from client
    clientMessage = await exitPromise;
  } finally {
    // teardown runs on both completion and abort
    stopExternalFlows();
    // remove transaction ui as soon as possible
    closeIframe(iframeEl, { skipActiveElement: isNestedFlow });
    resetHistory();
    document.body.style.overflow = prevOverflow;
  }

  return clientMessage.data.payload;
};

export function listenForExternalFlows(
  iframe: HTMLIFrameElement,
  colorScheme?: ColorScheme,
) {
  let externalFlowInProgress = false;
  const handler = async (ev: MessageEvent) => {
    if (!ev.data || !allowedDomains.some((d) => ev.origin.endsWith(d))) return;
    if (iframe && ev.source !== iframe.contentWindow) return;
    if (ev.data.type !== 'OPEN_URL_EXTERNAL' || externalFlowInProgress) return;
    externalFlowInProgress = true;
    try {
      const clientPayload = await launchInIframe(ev.data.payload.url, {
        isNestedFlow: true,
        colorScheme,
      });
      (ev.source as Window).postMessage(
        { type: 'COMPLETE', payload: clientPayload },
        { targetOrigin: ev.origin },
      );
    } finally {
      externalFlowInProgress = false;
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
